<template>
  <div class="simple-search">
    <PVInputGroup>
      <PVInputText
        v-model="queryText"
        :placeholder="t('workspaceDocuments.picker.search')"
        @keydown.enter.prevent="() => search()"
      />
      <PVButton @click="() => search()">
        {{ t('workspaceDocuments.picker.searchButton') }}
      </PVButton>
    </PVInputGroup>

    <div
      v-if="shouldShowContent"
      class="simple-search__content"
      :class="{ 'simple-search__content--busy': showBusyOverlay }"
    >
      <div class="simple-search__filters">
        <h3>{{ t('workspaceDocuments.picker.filters') }}</h3>
        <template v-if="showLoadingSkeletons">
          <workspace-document-search-refiner-skeleton
            v-for="index in loadingRefinerCount"
            :key="index"
          />
        </template>
        <template v-else>
          <workspace-document-search-refiner
            v-for="cfg in availableRefinerConfigs"
            :key="cfg.key"
            v-model="selectedRefiners[cfg.key]"
            :label="cfg.label"
            :refiner-values="refiners[cfg.key]"
          />
          <div v-if="workspaceLocation" class="simple-search__kks-filter">
            <label class="font-bold">{{
              t('workspaceDocuments.picker.kks')
            }}</label>
            <kks-selector
              v-model="selectedKks"
              :label="t('workspaceDocuments.picker.kks')"
              :location="workspaceLocation"
              :show-filter-button="false"
              :workspace-id="workspaceId"
            />
          </div>
          <PVButton class="simple-search__apply-btn" @click="() => search()">
            {{ t('workspaceDocuments.picker.applyFilters') }}
          </PVButton>
        </template>
      </div>

      <div class="simple-search__results">
        <PVPaginator
          v-if="searchResults.length"
          :first="skip"
          :rows="limit"
          :total-records="totalCount"
          @page="onPage"
        />

        <div v-if="showLoadingSkeletons" class="simple-search__cards">
          <search-result-card-skeleton
            v-for="index in loadingCardCount"
            :key="index"
          />
        </div>

        <div v-else-if="searchResults.length" class="simple-search__cards">
          <workspace-document-search-result-card
            v-for="document in searchResults"
            :key="document._id"
            :data="document"
            :mode="props.mode"
            :selected="isSelected(document._id)"
            :workspace-id="props.workspaceId"
            @toggle="toggleSelection"
          />
        </div>

        <div v-else class="simple-search__empty-state">
          {{ t('workspaceDocuments.picker.noResults') }}
        </div>

        <PVPaginator
          v-if="searchResults.length"
          :first="skip"
          :rows="limit"
          :total-records="totalCount"
          @page="onPage"
        />
      </div>

      <div
        v-if="showBusyOverlay"
        class="simple-search__busy-overlay"
        data-testid="workspace-document-picker-busy-overlay"
      >
        <div class="simple-search__busy-overlay-indicator">
          <PVProgressSpinner />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import axios, { AxiosResponse } from 'axios';
  import { computed, ref, watch } from 'vue';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    IWorkspaceDocumentSearchRequestDto,
    IWorkspaceDocumentSearchResponseDto,
    IWorkspaceDocumentSearchResultItemDto,
    WorkspaceDocumentPickerMode,
  } from 'types';
  import { useI18n } from 'vue-i18n';
  import { endpointsList } from '@/helpers';
  import { useToast } from '@/composables/useToast';
  import PVPaginator, { PageState } from 'primevue/paginator';
  import PVProgressSpinner from 'primevue/progressspinner';
  import PVButton from 'primevue/button';
  import PVInputGroup from 'primevue/inputgroup';
  import PVInputText from 'primevue/inputtext';
  import SearchResultCardSkeleton from './SearchResultCardSkeleton.vue';
  import WorkspaceDocumentSearchRefinerSkeleton from './WorkspaceDocumentSearchRefinerSkeleton.vue';
  import WorkspaceDocumentSearchRefiner from './WorkspaceDocumentSearchRefiner.vue';
  import WorkspaceDocumentSearchResultCard from './WorkspaceDocumentSearchResultCard.vue';

  const props = defineProps<{
    isVisible: boolean;
    mode: WorkspaceDocumentPickerMode;
    workspaceId: string;
    workspaceLocation: string;
  }>();

  const emit = defineEmits<{
    (
      e: 'selection-change',
      value: IWorkspaceDocumentSearchResultItemDto[],
    ): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const queryText = ref('');
  const searchResults = ref<IWorkspaceDocumentSearchResultItemDto[]>([]);
  const searchInProgress = ref(false);
  const initialLoadDone = ref(false);
  const refiners = ref<Record<string, Record<string, number>>>({});
  const selectedRefiners = ref<Record<string, string[]>>({});
  const selectedKks = ref('');
  const selectedDocumentsById = ref<
    Record<string, IWorkspaceDocumentSearchResultItemDto>
  >({});
  const totalCount = ref(0);
  const skip = ref(0);
  const limit = ref(10);
  const loadingCardCount = 5;
  const loadingRefinerCount = 3;

  const refinerConfigs = computed(() => [
    {
      key: 'docLanguage.label',
      label: t('workspaceDocuments.picker.refiners.docLanguage'),
    },
    {
      key: 'sourceSystem.label',
      label: t('workspaceDocuments.picker.refiners.sourceSystem'),
    },
    {
      key: 'responsibleArea.label',
      label: t('workspaceDocuments.picker.refiners.responsibleArea'),
    },
    {
      key: 'processStatus.label',
      label: t('workspaceDocuments.picker.refiners.processStatus'),
    },
    {
      key: 'docStatus.label',
      label: t('workspaceDocuments.picker.refiners.docStatus'),
    },
  ]);

  const availableRefinerConfigs = computed(() =>
    refinerConfigs.value.filter(
      (cfg) => Object.keys(refiners.value[cfg.key] ?? {}).length,
    ),
  );

  const selectedDocuments = computed(() =>
    Object.values(selectedDocumentsById.value),
  );
  const showLoadingSkeletons = computed(
    () => searchInProgress.value && !initialLoadDone.value,
  );
  const showBusyOverlay = computed(
    () => searchInProgress.value && initialLoadDone.value,
  );
  const shouldShowContent = computed(
    () => initialLoadDone.value || showLoadingSkeletons.value,
  );

  watch(selectedDocuments, (value) => emit('selection-change', value));
  watch(
    () => props.isVisible,
    (isVisible, wasVisible) => {
      if (!isVisible || wasVisible) {
        return;
      }

      refreshResults();
    },
    {
      immediate: true,
    },
  );

  function toSelectedKksArray(): string[] {
    return selectedKks.value
      .split(DEFAULT_METADATA_VALUES_SEPARATOR)
      .map((value) => value.trim())
      .filter(
        (value, index, values) => value && values.indexOf(value) === index,
      );
  }

  async function search(retainSkip = false): Promise<void> {
    if (!retainSkip) {
      skip.value = 0;
    }

    searchInProgress.value = true;

    const selectedKksValues = toSelectedKksArray();
    const normalizedQuery = queryText.value.trim();
    const payload: IWorkspaceDocumentSearchRequestDto = {
      limit: limit.value,
      skip: skip.value,
      refiners: refinerConfigs.value.map((config) => config.key),
      selectedRefiners: selectedRefiners.value,
      ...(normalizedQuery ? { queryText: normalizedQuery } : {}),
      ...(selectedKksValues.length ? { kks: selectedKksValues } : {}),
    };

    try {
      const response: AxiosResponse<IWorkspaceDocumentSearchResponseDto> =
        await axios.post(
          endpointsList.workspaceUploads.searchWorkspaceDocuments(
            props.workspaceId,
          ),
          payload,
        );

      initialLoadDone.value = true;
      searchResults.value = response.data.result;
      totalCount.value = response.data.count;
      refiners.value = response.data.refiners ?? {};

      for (const config of refinerConfigs.value) {
        selectedRefiners.value[config.key] ??= [];
      }
    } catch {
      createErrorToast(t('workspaceDocuments.picker.errors.searchFailed'));
    } finally {
      searchInProgress.value = false;
    }
  }

  function onPage(event: PageState): void {
    skip.value = event.first;
    limit.value = event.rows;
    search(true);
  }

  function isSelected(documentId: string): boolean {
    return !!selectedDocumentsById.value[documentId];
  }

  function toggleSelection(
    document: IWorkspaceDocumentSearchResultItemDto,
  ): void {
    if (isSelected(document._id)) {
      delete selectedDocumentsById.value[document._id];
    } else {
      selectedDocumentsById.value[document._id] = document;
    }
  }

  function resetSelection(): void {
    selectedDocumentsById.value = {};
  }

  function refreshResults(): void {
    search(true);
  }

  defineExpose({ resetSelection });
</script>

<style scoped lang="scss">
  .simple-search {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__content {
      position: relative;
      display: grid;
      grid-template-columns: 18rem minmax(0, 1fr);
      column-gap: 1.25rem;
      margin-top: 0.5rem;

      &--busy {
        pointer-events: none;

        .simple-search__filters,
        .simple-search__results {
          opacity: 0.45;
        }
      }
    }

    &__filters {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      border-right: 1px solid var(--p-surface-300);
      padding-right: 1rem;

      h3 {
        margin: 0;
      }
    }

    &__kks-filter {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-top: 0.5rem;
    }

    &__apply-btn {
      margin-top: 0.5rem;
    }

    &__results {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      min-width: 0;
    }

    &__empty-state {
      display: flex;
      justify-content: center;
      padding: 2rem;
      color: $inactive-font-color;
    }

    &__cards {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__busy-overlay {
      position: absolute;
      inset: 0;
      background: rgb(255 255 255 / 45%);
      backdrop-filter: blur(1px);
      z-index: 1;
    }

    &__busy-overlay-indicator {
      position: sticky;
      top: 2rem;
      display: flex;
      justify-content: center;
      padding-top: 2rem;
    }

    :deep(.p-paginator) {
      justify-content: center;
    }
  }
</style>
